import { Injectable, signal, inject } from '@angular/core';
import { Subject } from 'rxjs';
import {
  NotificationType,
  ActiveNotification,
  IndividualConfig,
} from 'wflow-cdk';
import { ProductEventTrackingService } from 'wflow-features/product-analytics';
import { WfToastTimerService } from './toast-timer.service';

export interface WfToast {
  id: number;
  message: string;
  title?: string;
  type: NotificationType;
  duration?: number;
  errorJson?: string;
  /**
   * Error identifier of an error toast (`error-code-<status>`), carried through
   * from the error handler via `config.messageClass`. Used as the analytics value
   * when the user copies the toast.
   */
  errorKey?: string;
  progressBar?: boolean;
  closeButton?: boolean;
  tapToDismiss?: boolean;
  onActivateTick?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class WfNotificationService {
  private readonly timerService = inject(WfToastTimerService);
  private readonly productTrackingSvc = inject(ProductEventTrackingService);

  readonly toasts = signal<WfToast[]>([]);
  private toastCounter = 0;

  /**
   * Show a success toast notification
   */
  success(
    message: string,
    title?: string,
    config?: Partial<IndividualConfig>,
  ): ActiveNotification<any> {
    return this.enqueueToast('success', message, title, config);
  }

  /**
   * Show an error toast notification
   */
  error(
    message: string,
    title?: string,
    config?: Partial<IndividualConfig>,
    errorJson?: string,
  ): ActiveNotification<any> {
    return this.enqueueToast('error', message, title, config, errorJson);
  }

  /**
   * Show an info toast notification
   */
  info(
    message: string,
    title?: string,
    config?: Partial<IndividualConfig>,
  ): ActiveNotification<any> {
    return this.enqueueToast('info', message, title, config);
  }

  /**
   * Show a warning toast notification
   */
  warning(
    message: string,
    title?: string,
    config?: Partial<IndividualConfig>,
  ): ActiveNotification<any> {
    return this.enqueueToast('warning', message, title, config);
  }

  /**
   * Remove a specific toast by id
   */
  remove(toastId: number): void {
    this.timerService.clear(toastId);
    this.toasts.update((toasts) => toasts.filter((t) => t.id !== toastId));
  }

  /**
   * Patch a toast that is already on screen — used for long-running progress
   * where the text changes but the toast must not re-enter the pile.
   * `@for … track toast.id` reuses the DOM node, so no re-animation.
   */
  update(
    toastId: number,
    patch: Partial<Omit<WfToast, 'id'>>,
    options: { bringToFront?: boolean } = {},
  ): void {
    this.toasts.update((toasts) => {
      const target = toasts.find((toast) => toast.id === toastId);

      if (!target) {
        return toasts;
      }

      const updated = { ...target, ...patch };

      // Only the last toast in the pile renders its text, so a long-lived toast
      // that keeps being updated has to move back to the end — otherwise every
      // short-lived toast raised meanwhile blanks it.
      if (options.bringToFront) {
        return [...toasts.filter((toast) => toast.id !== toastId), updated];
      }

      return toasts.map((toast) => (toast.id === toastId ? updated : toast));
    });
  }

  /**
   * Clear all toasts
   */
  clear(): void {
    this.timerService.clearAll();
    this.toasts.set([]);
  }

  /**
   * Pause all toast timers
   */
  pause(): void {
    this.timerService.pause();
  }

  /**
   * Resume all toast timers
   */
  resume(): void {
    this.timerService.resume();
  }

  /**
   * Private helper to show typed notifications
   */
  private enqueueToast(
    type: NotificationType,
    message: string,
    title?: string,
    config?: Partial<IndividualConfig>,
    errorJson?: string,
  ): ActiveNotification<any> {
    // Area P [Frustration] — error toast shown (central choke point for every
    // error toast across the app).
    if (type === 'error') {
      this.productTrackingSvc.track('comp_error_toast_shown', 1);
    }

    const toastId = ++this.toastCounter;
    const duration = config?.disableTimeOut ? 0 : (config?.timeOut ?? 5000);
    const progressBar = config?.progressBar ?? false;
    const closeButton = config?.closeButton ?? false;
    const tapToDismiss = config?.tapToDismiss ?? false;
    const onActivateTick = config?.onActivateTick ?? true;

    // Reduce long messages length to 500 characters and add ellipsis
    if (message.length > 500) {
      message = message.slice(0, 500).trim() + '...';
    }

    const toast: WfToast = {
      id: toastId,
      message,
      title,
      type,
      duration,
      errorJson,
      errorKey: config?.messageClass,
      progressBar,
      closeButton,
      tapToDismiss,
      onActivateTick,
    };

    this.toasts.update((toasts) => [...toasts, toast]);

    // Auto-remove after duration
    if (duration > 0) {
      this.timerService.start(toastId, duration, () => this.remove(toastId));
    }

    return this.createActiveNotification(toast);
  }

  /**
   * Create an ActiveNotification object compatible with the NotificationProvider interface
   */
  private createActiveNotification(toast: WfToast): ActiveNotification<any> {
    const onShown = new Subject<void>();
    const onHidden = new Subject<void>();
    const onTap = new Subject<void>();
    const onAction = new Subject<any>();

    // Emit shown immediately
    setTimeout(() => onShown.next(), 0);

    return {
      toastId: toast.id,
      title: toast.title ?? '',
      message: toast.message,
      portal: null as any,
      toastRef: null as any,
      onShown: onShown.asObservable(),
      onHidden: onHidden.asObservable(),
      onTap: onTap.asObservable(),
      onAction: onAction.asObservable(),
    };
  }
}
